export type SharedCacheMode = "default" | "bypass" | "reload";
export type SharedFetchDebugPhase =
  | "cache-hit"
  | "cache-miss"
  | "cache-skip-mode"
  | "inflight-join"
  | "network-start"
  | "network-success"
  | "network-error"
  | "cache-store"
  | "cache-bypass";

export type SharedFetchDebugEvent = {
  phase: SharedFetchDebugPhase;
  key: string;
  url: string;
  cacheMode: SharedCacheMode;
  cacheTtlMs: number;
  cacheMaxEntries: number;
  status?: number;
  error?: unknown;
};

export interface SharedFetchTextOptions {
  cacheMode?: SharedCacheMode;
  cacheTtlMs?: number;
  cacheMaxEntries?: number;
  signal?: AbortSignal;
  timeoutMs?: number;
  credentials?: RequestCredentials;
  headers?: HeadersInit;
  debug?: (event: SharedFetchDebugEvent) => void;
}

export interface SharedFetchTextResult {
  bodyText: string;
  fromCache: boolean;
  responseUrl: string;
  status: number;
  headers: Headers;
}

export class SharedFetchHttpError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly url: string;

  constructor(status: number, statusText: string, url: string) {
    super(`Request failed: ${status} ${statusText}`);
    this.name = "SharedFetchHttpError";
    this.status = status;
    this.statusText = statusText;
    this.url = url;
  }
}

export function isSharedFetchHttpError(error: unknown): error is SharedFetchHttpError {
  return error instanceof SharedFetchHttpError;
}

type HeaderRecord = Record<string, string>;

type CacheEntry = {
  data: Omit<SharedFetchTextResult, "fromCache" | "headers"> & { headers: HeaderRecord };
  expiresAt: number;
};

type Consumer = {
  resolve: (value: SharedFetchTextResult) => void;
  reject: (reason: unknown) => void;
  signal?: AbortSignal;
  abortHandler?: () => void;
};

type InflightEntry = {
  controller: AbortController;
  consumers: Set<Consumer>;
  done: boolean;
  timeoutId: number | null;
};

const DEFAULT_CACHE_TTL_MS = 10_000;
const DEFAULT_CACHE_MAX_ENTRIES = 200;
const DEFAULT_TIMEOUT_MS = 12_000;

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, InflightEntry>();

const stats = {
  networkRequests: 0,
};

export function clearSharedFetchCache(): void {
  cache.clear();
  for (const entry of inflight.values()) {
    if (entry.timeoutId !== null) {
      clearTimeout(entry.timeoutId);
      entry.timeoutId = null;
    }

    if (!entry.done) {
      entry.controller.abort(createAbortError("Cleared shared fetch cache"));
    }
  }
  inflight.clear();
  stats.networkRequests = 0;
}

export function getSharedFetchStats(): {
  cacheEntries: number;
  inflightEntries: number;
  networkRequests: number;
} {
  return {
    cacheEntries: cache.size,
    inflightEntries: inflight.size,
    networkRequests: stats.networkRequests,
  };
}

export function sharedFetchText(
  url: string,
  options: SharedFetchTextOptions = {}
): Promise<SharedFetchTextResult> {
  const normalizedUrl = normalizeUrl(url);
  const key = createFetchKey(normalizedUrl, options);
  const cacheMode = options.cacheMode ?? "default";
  const cacheTtlMs = Math.max(0, options.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS);
  const cacheMaxEntries = Math.max(1, options.cacheMaxEntries ?? DEFAULT_CACHE_MAX_ENTRIES);
  const emitDebug = (
    phase: SharedFetchDebugPhase,
    extra: Partial<Omit<SharedFetchDebugEvent, "phase" | "key" | "url" | "cacheMode" | "cacheTtlMs" | "cacheMaxEntries">> = {}
  ): void => {
    options.debug?.({
      phase,
      key,
      url: normalizedUrl,
      cacheMode,
      cacheTtlMs,
      cacheMaxEntries,
      ...extra,
    });
  };

  if (cacheMode === "default") {
    const hit = getCachedResult(key);
    if (hit) {
      emitDebug("cache-hit", { status: hit.status });
      return Promise.resolve(hit);
    }
    emitDebug("cache-miss");
  } else {
    emitDebug("cache-skip-mode");
  }

  const active = inflight.get(key);
  if (active) {
    emitDebug("inflight-join");
    return attachConsumer(active, options.signal);
  }

  const entry: InflightEntry = {
    controller: new AbortController(),
    consumers: new Set<Consumer>(),
    done: false,
    timeoutId: null,
  };

  inflight.set(key, entry);

  const timeoutMs = Math.max(0, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  if (timeoutMs > 0) {
    entry.timeoutId = window.setTimeout(() => {
      if (!entry.done) {
        entry.controller.abort(createAbortError(`Request timeout after ${timeoutMs}ms`));
      }
    }, timeoutMs);
  }

  void runNetworkFetch(
    normalizedUrl,
    options,
    cacheMode,
    cacheTtlMs,
    cacheMaxEntries,
    key,
    entry,
    emitDebug
  );

  return attachConsumer(entry, options.signal);
}

async function runNetworkFetch(
  normalizedUrl: string,
  options: SharedFetchTextOptions,
  cacheMode: SharedCacheMode,
  cacheTtlMs: number,
  cacheMaxEntries: number,
  key: string,
  entry: InflightEntry,
  emitDebug: (
    phase: SharedFetchDebugPhase,
    extra?: Partial<
      Omit<SharedFetchDebugEvent, "phase" | "key" | "url" | "cacheMode" | "cacheTtlMs" | "cacheMaxEntries">
    >
  ) => void
): Promise<void> {
  try {
    stats.networkRequests += 1;
    emitDebug("network-start");

    const response = await fetch(normalizedUrl, {
      method: "GET",
      signal: entry.controller.signal,
      credentials: options.credentials,
      headers: options.headers,
    });

    if (!response.ok) {
      throw new SharedFetchHttpError(
        response.status,
        response.statusText,
        response.url || normalizedUrl
      );
    }

    const bodyText = await response.text();

    const result: SharedFetchTextResult = {
      bodyText,
      fromCache: false,
      responseUrl: response.url || normalizedUrl,
      status: response.status,
      headers: cloneHeaders(response.headers),
    };

    if (cacheMode !== "bypass") {
      setCachedResult(key, result, cacheTtlMs, cacheMaxEntries);
      emitDebug("cache-store", { status: result.status });
    } else {
      emitDebug("cache-bypass", { status: result.status });
    }

    emitDebug("network-success", { status: result.status });
    settleConsumers(entry, { type: "resolve", value: result });
  } catch (error) {
    emitDebug("network-error", { error });
    settleConsumers(entry, { type: "reject", value: error });
  } finally {
    entry.done = true;
    if (entry.timeoutId !== null) {
      clearTimeout(entry.timeoutId);
    }
    inflight.delete(key);
  }
}

function attachConsumer(entry: InflightEntry, signal?: AbortSignal): Promise<SharedFetchTextResult> {
  return new Promise<SharedFetchTextResult>((resolve, reject) => {
    const consumer: Consumer = {
      resolve,
      reject,
      signal,
    };

    if (signal?.aborted) {
      reject(signal.reason ?? createAbortError());
      return;
    }

    if (signal) {
      const onAbort = () => {
        removeConsumer(entry, consumer);
        reject(signal.reason ?? createAbortError());
      };
      consumer.abortHandler = onAbort;
      signal.addEventListener("abort", onAbort, { once: true });
    }

    entry.consumers.add(consumer);
  });
}

function removeConsumer(entry: InflightEntry, consumer: Consumer): void {
  if (!entry.consumers.delete(consumer)) return;

  if (consumer.signal && consumer.abortHandler) {
    consumer.signal.removeEventListener("abort", consumer.abortHandler);
  }

  if (!entry.done && entry.consumers.size === 0) {
    entry.controller.abort(createAbortError("All consumers aborted"));
  }
}

function settleConsumers(
  entry: InflightEntry,
  result: { type: "resolve"; value: SharedFetchTextResult } | { type: "reject"; value: unknown }
): void {
  const consumers = Array.from(entry.consumers);
  entry.consumers.clear();

  for (let i = 0; i < consumers.length; i++) {
    const consumer = consumers[i];
    if (consumer.signal && consumer.abortHandler) {
      consumer.signal.removeEventListener("abort", consumer.abortHandler);
    }

    if (result.type === "resolve") {
      consumer.resolve(result.value);
    } else {
      consumer.reject(result.value);
    }
  }
}

function getCachedResult(key: string): SharedFetchTextResult | null {
  const item = cache.get(key);
  if (!item) return null;

  if (item.expiresAt < Date.now()) {
    cache.delete(key);
    return null;
  }

  cache.delete(key);
  cache.set(key, item);

  return {
    bodyText: item.data.bodyText,
    fromCache: true,
    responseUrl: item.data.responseUrl,
    status: item.data.status,
    headers: new Headers(item.data.headers),
  };
}

function setCachedResult(
  key: string,
  result: SharedFetchTextResult,
  ttlMs: number,
  maxEntries: number
): void {
  evictExpiredEntries();

  cache.delete(key);
  cache.set(key, {
    data: {
      bodyText: result.bodyText,
      responseUrl: result.responseUrl,
      status: result.status,
      headers: headersToRecord(result.headers),
    },
    expiresAt: Date.now() + ttlMs,
  });

  while (cache.size > maxEntries) {
    const oldest = cache.keys().next().value;
    if (!oldest) break;
    cache.delete(oldest);
  }
}

function evictExpiredEntries(): void {
  const now = Date.now();
  for (const [key, value] of cache.entries()) {
    if (value.expiresAt < now) {
      cache.delete(key);
    }
  }
}

function createFetchKey(url: string, options: SharedFetchTextOptions): string {
  const canonicalGetUrl = canonicalizeGetUrlForKey(url);
  const credentials = options.credentials ?? "same-origin";
  return `GET::${canonicalGetUrl}::${credentials}::${serializeHeaders(options.headers)}`;
}

function canonicalizeGetUrlForKey(url: string): string {
  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.href : "http://localhost");
    const entries = Array.from(parsed.searchParams.entries());
    entries.sort((a, b) => {
      if (a[0] === b[0]) return a[1].localeCompare(b[1]);
      return a[0].localeCompare(b[0]);
    });

    const canonicalQuery = new URLSearchParams();
    for (let i = 0; i < entries.length; i++) {
      canonicalQuery.append(entries[i][0], entries[i][1]);
    }

    const query = canonicalQuery.toString();
    return query
      ? `${parsed.origin}${parsed.pathname}?${query}`
      : `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url;
  }
}

function serializeHeaders(headers: HeadersInit | undefined): string {
  if (!headers) return "";
  const list: [string, string][] = [];

  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      list.push([key.toLowerCase(), value]);
    });
  } else if (Array.isArray(headers)) {
    for (let i = 0; i < headers.length; i++) {
      list.push([headers[i][0].toLowerCase(), headers[i][1]]);
    }
  } else {
    for (const key of Object.keys(headers)) {
      const value = headers[key];
      if (value === undefined) continue;
      list.push([key.toLowerCase(), String(value)]);
    }
  }

  list.sort((a, b) => a[0].localeCompare(b[0]));
  return JSON.stringify(list);
}

function normalizeUrl(url: string): string {
  try {
    if (typeof window !== "undefined" && window.location?.href) {
      return new URL(url, window.location.href).toString();
    }
    return new URL(url, "http://localhost").toString();
  } catch {
    return url;
  }
}

function cloneHeaders(headers: Headers): Headers {
  const out = new Headers();
  headers.forEach((value, key) => {
    out.set(key, value);
  });
  return out;
}

function headersToRecord(headers: Headers): HeaderRecord {
  const out: HeaderRecord = {};
  headers.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

function createAbortError(message = "The operation was aborted"): Error {
  if (typeof DOMException !== "undefined") {
    return new DOMException(message, "AbortError");
  }
  const error = new Error(message);
  error.name = "AbortError";
  return error;
}
